/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useEffect, useMemo } from "react";
import { Paper, Typography } from "@mui/material";
import { ArrowBack, Delete, Edit, Download, LocationOn, Directions } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import CustomIconButton from "packages/novous_web/src/common/components/CustomIconButton";
import AlertToast from "packages/novous_web/src/common/components/Alert";
import DeleteConfirmationModal from "packages/novous_web/src/common/components/DeleteDialog";
import DownloadData from "packages/novous_web/src/common/components/DownloadData";
import { AlertColor } from "@mui/material";
import { RouteDetailMap } from "../Directories/components/RouteDetailMap";
import { API_ENDPOINTS } from "packages/novous_web/src/api/endpoints";
import useAxios from "packages/novous_web/src/api/useAxios";
import { useAuth } from "packages/novous_web/src/authContext/useAuth";
import TripDetailMap from "../Trips/componenets/TripDetailMap";
import { useGoogleMaps } from "packages/novous_web/src/context/GoogleMapContext";
import md5 from "md5";
import useApiError from "packages/novous_web/src/api/useApiError";

// Define a type for route details if needed
interface RouteDetails {
  approval: string | null;
  avgLoadTime: string | null;
  comment: string | null;
  routeDirectionId: string;
  customerId: string;
  deleteStatus: boolean;
  destination: string;
  destinationAddressCode: string;
  destinationLatitude: string;
  destinationLongitude: string;
  destinationName: string;
  encodedPolyline: any[];
  fleetAdminId: string;
  routeApprovalBy: string | null;
  routeCode: string;
  routeCreated: string | null;
  routeDistance: string;
  routeDuration: string;
  routeInUse: boolean;
  routeName: string;
  routeSelected: string | null;
  routeTolls: string | null;
  source: string;
  sourceAddressCode: string;
  sourceLatitude: string;
  sourceLongitude: string;
  sourceName: string;
  tag: string | null;
  vendor: string | null;
}

const RouteDetailsPage = () => {
  const navigate = useNavigate();

  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    type: AlertColor;
  }>({
    open: false,
    message: "",
    type: "error",
  });

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("pdf");
  const [routeData, setRouteData] = useState<RouteDetails | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const location = useLocation();
  const route = location.state || {};
  const { user } = useAuth();
  const fleetId = user?.customer_id;
  const fleetAdminId = user?.parentId;
  const { isLoaded } = useGoogleMaps();

  const {
    data: routeDetailsData,
    loading: routeDetailsLoading,
    error: routeDetailsError,
    fetchData: fetchRouteDetails,
  } = useAxios<any>(API_ENDPOINTS.directories.ROUTE_DETAILS, "POST");

  const {
    data: downloadRoutesData,
    error: downloadRoutesError,
    loading: downloadRoutesLoading,
    fetchData: fetchDownloadRoutesData,
  } = useApiError<any>(`${API_ENDPOINTS.directories.ROUTES_DOWNLOAD}`, 'POST', 'blob');

  useEffect(() => {
    if (downloadRoutesData) {
      try {
        if (downloadRoutesData instanceof Blob) {
          const url = window.URL.createObjectURL(downloadRoutesData);
          const a = document.createElement('a');
          a.href = url;
          let extension = selectedFormat.toLowerCase();
          if (extension === 'excel') {
            extension = 'xlsx'; // Use xlsx for Excel format
          }

          const fileName = `route_data.${extension}`;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          console.error('Invalid response format. Expected a Blob.');
        }
      } catch (error) {
        console.error('Error downloading file:', error);
      } finally {
        setIsDownloadModalOpen(false);
      }
    }
  }, [downloadRoutesData]);

  const handleDownload = async () => {
    setIsSendingEmail(true);
    const requestData = {
      fleetId: user?.customer_id,
      fileType: selectedFormat,
      routeCode: routeData?.routeCode
    };

    try {
      const response = await fetchDownloadRoutesData(requestData);
      if (
        response?.type === 'text/csv' ||
        response?.type === 'application/pdf' ||
        response?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        setToast({
          open: true,
          message: `File has been downloaded successfully`,
          type: 'success',
        })
      } else {
        setToast({
          open: true,
          message: response?.error,
          type: 'error',
        })
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setToast({
        open: true,
        message: (error as Error).message,
        type: 'error',
      })
    } finally {
      setIsSendingEmail(false);
    }
  };

  const {
    data: routeDeleteData,
    error: routeDeleteError,
    loading: routeDeleteLoading,
    fetchData: fetchRouteDelete,
  } = useAxios<any>(
    `${API_ENDPOINTS.directories.ROUTES_DELETE}`,
    "POST"
  );

  useEffect(() => {
    if (route?.routeCode && fleetId && fleetAdminId) {
      fetchRouteDetails({ routeCode: route.routeCode, fleetId, fleetAdminId });
      fetchRoutePathData({ routeCode: route.routeCode, fleetId, fleetAdminId })
    }
  }, [route, fleetId, fleetAdminId]);

  const {
    data: routePathData,
    error: routePathError,
    loading: routePathLoading,
    fetchData: fetchRoutePathData,
  } = useAxios<any>(`${API_ENDPOINTS.dashboard.ROUTE_PATH}`, "POST");

  useEffect(() => {
    if (routeDetailsData) {
      // Assuming the API returns an object with the route details
      console.log("Route Details API Response:", routeDetailsData.data);
      setRouteData(routeDetailsData.data);
    }
  }, [routeDetailsData]);


  // Helper function to format duration (assuming routeDuration is in seconds)
  const formatDuration = (durationInSeconds: string) => {
    const seconds = parseInt(durationInSeconds, 10);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} hours ${minutes} minutes`;
  };

  const handleDeleteRoute = async () => {

    try {
      const payload = {
        id: routeData?.routeCode,
        fleetId: fleetId,
        fleetAdminId: fleetAdminId,
      }

      await fetchRouteDelete(payload);
      setToast({ open: true, message: "Route deleted Successfully", type: "success" });
      setTimeout(() => navigate(-1), 1500);

    } catch (error) {
      setToast({ open: true, message: "Route deleted Failed", type: "error" });
    }

  };

  // const handleDownload = () => {
  //   // Implement actual download logic if needed
  //   console.log('Downloading Route');
  //   setIsDownloadModalOpen(false);
  // };

  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFormat(event.target.value);
  };

  // Fallback values if routeData is null
  const displayRouteName = useMemo(() => routeData?.routeName || "Loading route...", [routeData]);
  const displaySourceName = useMemo(() => routeData?.sourceName || "Loading source...", [routeData]);
  const displayDestinationName = useMemo(() => routeData?.destinationName || "Loading destination...", [routeData]);
  const displayRouteDistance = useMemo(() => routeData?.routeDistance ? `${routeData.routeDistance} km` : "", [routeData]);
  const displayRouteDuration = useMemo(() => routeData?.routeDuration ? formatDuration(routeData.routeDuration) : "", [routeData]);
  const displayRouteTolls = useMemo(() => routeData?.routeTolls ? routeData.routeTolls : "N/A", [routeData]);
  const displayComment = useMemo(() => routeData?.comment || "No comments available", [routeData]);

  // For the map, prepare start and end locations from API data if available
  const startLocation = useMemo(() => ({
    sourceCode: routeData?.sourceAddressCode || "",
    sourceLabel: displaySourceName,
    lat: routeData ? parseFloat(routeData.sourceLatitude) : 0,
    lng: routeData ? parseFloat(routeData.sourceLongitude) : 0,
  }), [routeData, displaySourceName]);

  const endLocation = useMemo(() => ({
    destinationCode: routeData?.destinationAddressCode || "",
    destinationLabel: displayDestinationName,
    lat: routeData ? parseFloat(routeData.destinationLatitude) : 0,
    lng: routeData ? parseFloat(routeData.destinationLongitude) : 0,
  }), [routeData, displayDestinationName]);

  // Fetch routes when start or end changes
  const [suggestedRoutes, setSuggestedRoutes] = useState<any[]>([]);

  useEffect(() => {
    if (!isLoaded || !startLocation.sourceLabel || !endLocation.destinationLabel) return;
    console.log("Fetching directions for:", startLocation.sourceLabel, endLocation.destinationLabel);

    const directionsService = new google.maps.DirectionsService();

    directionsService.route(
      {
        origin: startLocation.sourceLabel,
        destination: endLocation.destinationLabel,
        travelMode: google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: true,
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          const routes = result.routes.map((route, index) => ({
            routeId: btoa(`${route.summary}-${index}`).substring(0, 16),
            distance: route.legs[0].distance?.text || "N/A",
            duration: route.legs[0].duration?.text || "N/A",
            legs: route.legs,
            summary: route.summary || `Route ${index + 1}`,
            via: "Direct Route",
            directions: {
              ...result,
              routes: [route],
            },
            polylineData: route,
          }));
          setSuggestedRoutes(routes);
        } else {
          setSuggestedRoutes([]);
          console.error("Error fetching directions:", status);
        }
      }
    );
  }, [isLoaded, startLocation, endLocation]);

  function makeRouteKey(route: any) {
    // 1) Summary (string)
    const summary = route.summary || '';

    // 4) Encoded overview polyline
    const poly = route.polylineData || '';

    // Combine into one string
    const fingerprint = `${summary}|${poly}`;

    // Hash it
    return md5(fingerprint);
  }

  const selectedRoute = useMemo(() => {
    if (suggestedRoutes.length > 0 && routeData && routeData.routeDirectionId) {


      return suggestedRoutes.find(route => {
        const key = makeRouteKey(route);

        return key === routeData.routeDirectionId
      });
    }
    return null;
  }, [suggestedRoutes]);

  if (routeData)
    console.log(selectedRoute, 'selectedRoute yashu', suggestedRoutes, routeData.routeDirectionId);



  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <>
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type}
      />

      <DownloadData
        isDownloadModalOpen={isDownloadModalOpen}
        handleDownloadModalClose={() => setIsDownloadModalOpen(false)}
        selectedFormat={selectedFormat}
        handleFormatChange={handleFormatChange}
        handleDownload={handleDownload}
        modalTitle="Download Route Data"
        modalDescription="Please select a file format for your download."
        formatLabel="Format"
        formatExcelLabel="Excel"
        formatCsvLabel="CSV"
        formatPdfLabel="PDF"
        cancelButtonText="Cancel"
        downloadButtonText="Download"
      />

      <DeleteConfirmationModal
        open={openDeleteModal}
        onConfirm={() => {
          handleDeleteRoute();
          setOpenDeleteModal(false);
        }}
        onCancel={() => setOpenDeleteModal(false)}
      />

      <Paper elevation={0} className="p-4 md:p-8 h-full flex flex-col gap-4 rounded-md">
        {/* Header Section */}
        <div className="flex flex-col gap-2 mb-4">
          {/* Back Button Row */}
          <div>
            <CustomIconButton
              icon={<ArrowBack />}
              text="Back"
              background="transparent"
              outline={false}
              size={16}
              onClick={() => navigate(-1)}
            />
          </div>
          {/* Title and Actions Row */}
          <div className="flex justify-between items-center">
            <div>
              <Typography variant="h6" className="font-semibold">
                {displayRouteName}
              </Typography>
              <Typography variant="button" fontWeight={400} color="secondary">
                {routeData?.routeCreated ? `Created on : ${routeData.routeCreated}` : "Creation date not available"}
              </Typography>
            </div>
            <div className="flex gap-2">
              <CustomIconButton
                icon={<Delete />}
                text="Delete"
                color="error"
                variant="outlined"
                background="transparent"
                onClick={() => setOpenDeleteModal(true)}
                outline={false}
              />
              {/* <CustomIconButton
                icon={<Edit />}
                text="Edit"
                variant="outlined"
                background="transparent"
                onClick={() => alert("Edit in progress")}
              /> */}
              <CustomIconButton
                icon={<Download />}
                text="Download"
                variant="contained"
                onClick={() => setIsDownloadModalOpen(true)}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          {/* Left Section - Scrollable Container */}
          <div
            className="flex-1 flex flex-col gap-4 overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 200px)" }}
          >
            {/* Route Details */}
            <Paper className="flex flex-col p-4 gap-2 w-full bg-white border rounded-md">
              <Typography variant="h6" className="font-semibold text-[#1b1b1b]">
                Route Details
              </Typography>
              <div className="flex flex-col gap-1">
                <Typography variant="overline" color="secondary">
                  Route Name
                </Typography>
                <Typography variant="button" className="flex items-center text-[#1b1b1b]">
                  <Directions className="mr-1" /> {displayRouteName}
                </Typography>
              </div>
              {routeData?.tag && (
                <div className="flex flex-col gap-1">
                  <Typography variant="overline" color="secondary">
                    Route Tag
                  </Typography>
                  <div className="flex gap-2 flex-wrap">
                    <span className="px-2 py-1 bg-[#eeedfcff] text-[#353247] rounded-md">
                      {routeData.tag}
                    </span>
                  </div>
                </div>
              )}
            </Paper>

            {/* Source Details */}
            <Paper className="flex flex-col w-full p-4 gap-2 bg-white border rounded-md">
              <Typography variant="h6" className="font-semibold text-[#1b1b1b]">
                Source Details
              </Typography>
              <div className="flex flex-col gap-1">
                <Typography variant="overline" color="secondary">
                  Source Code
                </Typography>
                <Typography variant="button" className="flex items-center text-[#1b1b1b]">
                  <LocationOn className="mr-1" /> {routeData?.sourceAddressCode || "N/A"}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="overline" color="secondary">
                  Source Address
                </Typography>
                <Typography variant="button" className="flex items-center text-[#1b1b1b]">
                  <LocationOn className="mr-1" /> {displaySourceName}
                </Typography>
              </div>
            </Paper>

            {/* Destination Details */}
            <Paper className="flex flex-col w-full p-4 gap-2 bg-white border rounded-md">
              <Typography variant="h6" className="font-semibold text-[#1b1b1b]">
                Destination Details
              </Typography>
              <div className="flex flex-col gap-1">
                <Typography variant="overline" color="secondary">
                  Destination Code
                </Typography>
                <Typography variant="button" className="flex items-center text-[#1b1b1b]">
                  <LocationOn className="mr-1" /> {routeData?.destinationAddressCode || "N/A"}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="overline" color="secondary">
                  Destination Address
                </Typography>
                <Typography variant="button" className="flex items-center text-[#1b1b1b]">
                  <LocationOn className="mr-1" /> {displayDestinationName}
                </Typography>
              </div>
            </Paper>

            {/* Travel Metrics */}
            <Paper className="flex flex-col p-4 gap-2 w-full bg-white border rounded-md">
              <Typography variant="h6" className="font-semibold text-[#1B1B1B]">
                Travel Metrics
              </Typography>
              <div className="flex flex-col gap-1">
                <div className="flex gap-4">
                  {displayRouteDistance && (
                    <div className="flex-1 flex flex-col gap-1">
                      <Typography variant="overline" color="secondary">
                        Distance
                      </Typography>
                      <Typography variant="button" className="text-[#1B1B1B]">
                        {displayRouteDistance}
                      </Typography>
                    </div>
                  )}
                  {displayRouteDuration && (
                    <div className="flex-1 flex flex-col gap-1">
                      <Typography variant="overline" color="secondary">
                        Duration
                      </Typography>
                      <Typography variant="button" className="text-[#1B1B1B]">
                        {displayRouteDuration}
                      </Typography>
                    </div>
                  )}
                </div>
                <div className="flex gap-4">
                  <div className="flex-1 flex flex-col gap-1">
                    <Typography variant="overline" color="secondary">
                      Tolls
                    </Typography>
                    <Typography variant="button" className="text-[#1B1B1B]">
                      {displayRouteTolls}
                    </Typography>
                  </div>
                </div>
              </div>
            </Paper>

            {/* Comments */}
            <Paper className="flex flex-col w-full p-4 gap-2 bg-white border rounded-md">
              <Typography variant="h6" className="font-semibold text-[#1b1b1b]">
                Comments
              </Typography>
              <Typography variant="body2" className="text-[#767676]">
                {displayComment}
              </Typography>
            </Paper>
          </div>

          {/* Right Section - Map updated with RouteDetailMap */}
          <Paper elevation={1} className="flex-1 p-4 rounded-md">
            <TripDetailMap routeData={selectedRoute} stopsData={[]} />
          </Paper>
        </div>
      </Paper>
    </>
  );
};

export default RouteDetailsPage;
